import type { ConditionAlertRowViewModel, AlertRowState } from './AlertRowViewModel';
import type { ConditionAlertRowViewModelImpl } from './AlertRowViewModel/impl';
import type { IdeaAlertRowViewModel, IdeaAlertRowState } from './IdeaAlertRowViewModel';
import type { IdeaAlertRowViewModelImpl } from './IdeaAlertRowViewModel/impl';
import { stateToNotifications } from './IdeaAlertRowViewModel/state/types';
import type { AlertsSort, AlertsWidgetModel, AlertUpdateData } from './index';
import { parseMaxIdeasFromRoles, canEnableIdeaAlert, getIdeaAlertIdsToDisable } from './maxIdeas';
import { parseMaxSymbolsFromRoles, getActiveSymbols, canEnableAlert, getAlertIdsToDisable } from './maxSymbols';
import { buildNotifications } from './notifications';
import { signal, computed, effect, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import type { AlertDefinition, AlertNotification } from '@thinkalpha/platform-ws-client/contracts/alerts.js';
import type { ConcreteIndicatorViewModel } from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import { t } from 'i18next';
import { optional } from 'inversify';
import type { TableStatus } from 'src/components/table-view/model';
import { ReactiveInjectable, inject, injectable } from 'src/features/ioc';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { autoCreateAlertsFromChannel } from 'src/lib/featureFlags/flags/autoCreateAlertsFromChannel';
import { instrumentToString } from 'src/lib/instrumentUtils';
import type { ChannelSlice } from 'src/models/ChannelSlice';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import type { WidgetHeaderElement } from 'src/models/WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from 'src/models/WidgetModel/impl';
import type { WidgetTabSlice } from 'src/models/WidgetTabSlice';
import { getAlertingIndicatorsQuery } from 'src/queries/dictionary';
import type { AlertService } from 'src/services/AlertService';
import type { NewConditionAlertDefinition, NewIdeaAlertDefinition } from 'src/services/AlertService/types';
import type { FeatureFlagService } from 'src/services/FeatureFlagService';
import type { SlugMapService } from 'src/services/SlugMapService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { updateActiveAlertsTab } from 'src/store/actions/widgets/alerts';
import type { AlertsWidgetViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import { v4 as uuid } from 'uuid';

/** Constant used to indicate standalone mode when passed to init() */
export const STANDALONE_MODE_ID = 'standalone';

@injectable()
export class AlertsWidgetModelImpl extends ReactiveInjectable implements AlertsWidgetModel {
    readonly #alertingIndicatorsQuery: QueryObserverModel<ConcreteIndicatorViewModel[]>;
    readonly #alertRowViewModels = signal<ConditionAlertRowViewModelImpl[]>([]);
    /** Current sort state - default is oldest first by created date */
    readonly #currentSort = signal<AlertsSort>('createdAt asc');
    readonly activeAlertsTab = signal<'symbols' | 'ideas'>('symbols');
    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);

    readonly #maxSymbols = computed(() => parseMaxSymbolsFromRoles(this.activeUserModel.userRoles.value));
    readonly #activeSymbols = computed(() => getActiveSymbols(this.#alertRowViewModels.value));
    readonly #activeSymbolCount = computed(() => this.#activeSymbols.value.length);

    // Idea alerts
    readonly #ideaAlertRowViewModels = signal<IdeaAlertRowViewModelImpl[]>([]);
    #ideaManualSort = false;
    readonly #maxIdeas = computed(() => parseMaxIdeasFromRoles(this.activeUserModel.userRoles.value));
    readonly #activeIdeaCount = computed(
        () => this.#ideaAlertRowViewModels.value.filter((vm) => vm.enabled.value).length,
    );

    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    /** Flag to track if we're in standalone mode (no tab in store) */
    #isStandaloneMode = false;

    readonly #tabId = signal('');

    readonly #widgetState = computed(() => {
        const tabId = this.#tabId.value;
        if (!tabId) return undefined;
        return this.store.signal.value.tab.byId[tabId]?.widget as AlertsWidgetViewModel | undefined;
    });

    #autoCreateFromChannel: ReadonlySignal<boolean>;

    /** Maps AlertService worker connection status into the shared widget header
     * connection-status signal shape. The shared component doesn't know about
     * `'error'` — collapse it to `'disconnected'` so the icon renders the same
     * red-pulse state used by other widgets when their connection is broken. */
    readonly #connectionStatus: ReadonlySignal<{
        status: TableStatus;
        progress: number | undefined;
        failureCount: number;
    }> = computed(() => {
        const raw = this.alertService.connectionStatus.value;
        const status: TableStatus = raw === 'error' ? 'disconnected' : raw;
        return { status, progress: undefined, failureCount: 0 };
    });

    headerElements!: WidgetHeaderElement[];
    ideaTabHeaderElements!: WidgetHeaderElement[];

    eventBus!: AlertsWidgetModel['eventBus'];

    constructor(
        @inject('Store') private store: ReactBindings['Store'],
        @inject('Logger') private logger: ReactBindings['Logger'],
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('SlugMapService') private readonly slugMapService: SlugMapService,
        @inject('AlertService') private readonly alertService: AlertService,
        @inject('ConditionAlertRowViewModelFactory')
        private readonly alertRowViewModelFactory: () => ConditionAlertRowViewModelImpl,
        @inject('IdeaAlertRowViewModelFactory')
        private readonly ideaAlertRowViewModelFactory: () => IdeaAlertRowViewModelImpl,
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('ActiveUserModel') private readonly activeUserModel: ReactBindings['ActiveUserModel'],
        @inject('FeatureFlagService') private readonly featureFlagService: FeatureFlagService,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.logger = logger.getSubLogger({ name: 'AlertsWidgetModel' });

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }
        this.#alertingIndicatorsQuery = queryObserverModelFactory(getAlertingIndicatorsQuery());
        this.disposableStack.use(this.#alertingIndicatorsQuery);
        this.#autoCreateFromChannel = this.featureFlagService.getSignal(autoCreateAlertsFromChannel);

        this.eventBus = this.#buildEventBus();

        // Sync AlertRowViewModels from AlertService.conditionAlerts (ConditionAlertModels)
        this.disposableStack.defer(
            effect(() => {
                this.#syncAlertRowViewModels();
            }),
        );

        // Reactively enforce max symbols limit: when roles change or alerts change,
        // disable any enabled alerts whose symbol falls outside the first N unique symbols.
        this.disposableStack.defer(
            effect(() => {
                const maxSymbols = this.#maxSymbols.value;
                if (maxSymbols === undefined) return;

                const sortedAlerts = this.#alertRowViewModels.value;
                const idsToDisable = getAlertIdsToDisable(sortedAlerts, maxSymbols);
                if (idsToDisable.length === 0) return;

                if (TRACE) {
                    this.logger.trace('Max symbols limit exceeded, disabling excess alerts', {
                        maxSymbols,
                        disablingCount: idsToDisable.length,
                    });
                }

                untracked(() => {
                    for (const id of idsToDisable) {
                        const vm = sortedAlerts.find((a) => a.id === id);
                        vm?.dispatch({ type: 'SET_ENABLED', enabled: false });
                    }
                });
            }),
        );

        // Sync IdeaAlertRowViewModels from AlertService.ideaAlerts
        this.disposableStack.defer(
            effect(() => {
                this.#syncIdeaAlertRowViewModels();
            }),
        );

        // Enforce max ideas limit
        this.disposableStack.defer(
            effect(() => {
                const maxIdeas = this.#maxIdeas.value;
                if (maxIdeas === undefined) return;

                const vms = this.#ideaAlertRowViewModels.value;
                const idsToDisable = getIdeaAlertIdsToDisable(vms, maxIdeas);
                if (idsToDisable.length === 0) return;

                untracked(() => {
                    for (const id of idsToDisable) {
                        const vm = vms.find((a) => a.id === id);
                        vm?.dispatch({ type: 'SET_ENABLED', enabled: false });
                    }
                });
            }),
        );

        // Clean up all row view models on dispose
        this.disposableStack.defer(() => {
            for (const vm of this.#alertRowViewModels.value) {
                vm[Symbol.dispose]();
            }
            for (const vm of this.#ideaAlertRowViewModels.value) {
                vm[Symbol.dispose]();
            }
        });
    }

    /**
     * Handle state changes from ConditionAlertRowViewModels.
     * Syncs the new state to the AlertService.
     */
    #handleRowStateChange = (alertId: string, state: AlertRowState): void => {
        // Don't sync if condition is null (shouldn't happen for initialized alerts)
        if (state.condition === null) {
            this.logger.warn('Attempted to sync alert with null condition', { alertId });
            return;
        }

        // Enforce max symbols limit: if an alert is being enabled and it would push
        // us over the limit, revert the enable in the VM and sync it as disabled.
        // We still sync other changes (e.g. condition value) with enabled: false.
        let effectiveEnabled = state.enabled;
        if (state.enabled) {
            const vm = this.#alertRowViewModels.value.find((a) => a.id === alertId);
            if (vm) {
                const maxSymbols = this.#maxSymbols.value;
                const activeSymbols = this.#activeSymbols.value;
                if (!canEnableAlert(vm.instrumentString, activeSymbols, maxSymbols)) {
                    if (TRACE) {
                        this.logger.trace('Blocked alert enable: would exceed max symbols limit', {
                            alertId,
                            symbol: vm.instrumentString,
                            maxSymbols,
                            activeSymbolCount: activeSymbols.length,
                        });
                    }
                    // Revert the enable in the VM (doesn't re-trigger handleRowStateChange
                    // because SYNC_FROM_DEFINITION is excluded; SET_ENABLED will fire again
                    // but effectiveEnabled will be false so no infinite loop)
                    vm.dispatch({ type: 'SET_ENABLED', enabled: false });
                    effectiveEnabled = false;
                }
            }
        }

        // Build API update from state
        const apiUpdates: Partial<NewConditionAlertDefinition> = {
            condition: state.condition,
            enabled: effectiveEnabled,
            notifications: buildNotifications({
                showToast: state.showToast,
                playSound: state.playSound,
                soundType: state.soundType,
                speakTts: state.speakTts,
                ttsText: state.ttsText,
                ttsVoiceUri: state.ttsVoiceUri,
                ttsRate: state.ttsRate,
                ttsPitch: state.ttsPitch,
                ttsVolume: state.ttsVolume,
            }),
            userNote: state.note ?? '',
        };

        if (TRACE) {
            this.logger.trace('Syncing alert state change to service', { alertId, apiUpdates });
        }

        // Fire and forget - ConditionAlertModel will be updated via the subscription when server responds
        this.alertService.updateAlert(alertId, apiUpdates).catch((err) => {
            this.logger.error('Failed to sync alert state change', { alertId, error: err });
        });
    };

    #syncAlertRowViewModels(): void {
        const alertModels = this.alertService.conditionAlerts.value;
        if (TRACE) {
            this.logger.trace('Syncing AlertRowViewModels', {
                serviceAlertCount: alertModels.length,
            });
        }
        // Use untracked to read current view models without creating a dependency cycle
        const currentViewModels = untracked(() => this.#alertRowViewModels.value);
        const currentVmMap = new Map<string, ConditionAlertRowViewModelImpl>(
            currentViewModels.map((vm) => [vm.id, vm]),
        );

        const newViewModels: ConditionAlertRowViewModelImpl[] = [];
        const seenIds = new Set<string>();

        for (const model of alertModels) {
            seenIds.add(model.id);

            const existingVm = currentVmMap.get(model.id);
            if (existingVm) {
                // Keep existing view model (it already wraps this model and will react to changes)
                newViewModels.push(existingVm);
            } else {
                // Create new ConditionAlertRowViewModel wrapping this ConditionAlertModel
                const newVm = this.alertRowViewModelFactory();
                newVm.initFromConditionAlertModel(model);
                // Set up callback to sync state changes to AlertService
                newVm.setOnStateChange(this.#handleRowStateChange);
                newViewModels.push(newVm);
            }
        }

        // Dispose view models whose ConditionAlertModel no longer exists
        for (const vm of currentViewModels) {
            if (!seenIds.has(vm.id)) {
                vm[Symbol.dispose]();
            }
        }

        // Sort by current sort state
        const currentSort = this.#currentSort.value;
        if (currentSort === 'manual') {
            // In manual mode, preserve existing order for known VMs; new VMs go at the end
            const currentVmOrder = untracked(() => this.#alertRowViewModels.value);
            const indexById = new Map<string, number>(currentVmOrder.map((vm, i) => [vm.id, i]));
            newViewModels.sort((a, b) => {
                const ai = indexById.get(a.id) ?? -1;
                const bi = indexById.get(b.id) ?? -1;
                // Unknown VMs (new) go at end
                if (ai === -1 && bi === -1) return 0;
                if (ai === -1) return 1;
                if (bi === -1) return -1;
                return ai - bi;
            });
        } else {
            newViewModels.sort((a, b) => this.#compareAlerts(a, b, currentSort));
        }

        this.#alertRowViewModels.value = newViewModels;

        if (TRACE) {
            this.logger.trace('Synced AlertRowViewModels', {
                count: newViewModels.length,
            });
        }
    }

    readonly alertsEnabled = computed(() => !this.alertService.suppressNotifications.value);

    get maxSymbols(): ReadonlySignal<number | undefined> {
        return this.#maxSymbols;
    }

    get activeSymbolCount(): ReadonlySignal<number> {
        return this.#activeSymbolCount;
    }

    readonly #symbolFilter = signal('');

    get alerts(): ReadonlySignal<ConditionAlertRowViewModel[]> {
        return this.#alertRowViewModels;
    }

    readonly filteredAlerts = computed(() => {
        const filterValue = this.#symbolFilter.value;
        const alerts = this.#alertRowViewModels.value;
        if (!filterValue) return alerts;
        const lowerFilter = filterValue.toLowerCase();
        return alerts.filter((alert) => alert.instrumentString.toLowerCase().includes(lowerFilter));
    });

    readonly #alertingIndicators = computed(() => this.#alertingIndicatorsQuery.signal.value.data ?? []);
    get alertingIndicators(): ReadonlySignal<ConcreteIndicatorViewModel[]> {
        return this.#alertingIndicators;
    }

    readonly #highlightedAlertId = signal<string | null>(null);
    readonly #pendingFocusAlertIds = new Set<string>();
    get highlightedAlertId(): ReadonlySignal<string | null> {
        return this.#highlightedAlertId;
    }

    consumePendingFocus(alertId: string): boolean {
        return this.#pendingFocusAlertIds.delete(alertId);
    }

    #buildEventBus(): AlertsWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Handle instrument change by creating an alert for the symbol
                .addEvent('widget::instrumentChange', (payload) => {
                    if (payload.instrument) {
                        const instrument: Instrument = { type: 'equity', symbol: payload.instrument };
                        this.addAlertForInstrument(instrument).catch((err) => {
                            this.logger.error('Failed to auto-create alert for instrument change', {
                                instrument: instrumentToString(instrument),
                                error: err,
                            });
                        });
                    }
                })
                // Signal for instrument - always returns empty string as Alerts widget doesn't track a "current instrument"
                .addSignal('widget::instrument', () => computed(() => ''))
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                // Global alerts kill-switch
                .addSignal('widget::alertsEnabled', () =>
                    computed(() => !this.alertService.suppressNotifications.value),
                )
                .addEvent('widget::toggleAlertsEnabled', ({ enabled }) => {
                    this.alertService.suppressNotifications.value = !enabled;
                })
                // AlertService worker connection status (consumed by the
                // 'connectionStatus' header element).
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                // Symbol filter
                .addSignal('widget::symbolFilter', () => this.#symbolFilter)
                .addEvent('widget::updateSymbolFilter', ({ filter }) => {
                    this.#symbolFilter.value = filter;
                })
                // Ideas tab switcher
                .addSignal('widget::activeAlertsTab', () => this.activeAlertsTab)
                .addEvent('widget::changeAlertsTab', ({ tab }) => {
                    if (tab === this.activeAlertsTab.value) return;
                    this.activeAlertsTab.value = tab;
                    if (!this.#isStandaloneMode) {
                        this.store.dispatch(updateActiveAlertsTab(this.#tabId.value, tab));
                    }
                })
                // Add idea alert from header button
                .addEvent('widget::addIdeaAlert', () => {
                    this.addIdeaAlert({
                        type: 'idea',
                        slowIdeaId: null,
                        fastIdeaId: null,
                        triggerMode: 'enter',
                        enabled: false,
                        notifications: [{ type: 'toast' }, { type: 'sound', sound: 'tambourine' }],
                    }).catch((error) => {
                        this.logger.error('Failed to add idea alert', { error });
                    });
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus);
    }

    init(tabId: string): void {
        // Check if we're in standalone mode by looking for the explicit constant
        this.#isStandaloneMode = tabId === STANDALONE_MODE_ID;
        this.#tabId.value = tabId;

        // Restore persisted activeAlertsTab from store (defaults to 'symbols' if not set)
        this.activeAlertsTab.value = this.#widgetState.value?.activeAlertsTab ?? 'symbols';

        // Build headerElements based on mode — standalone pages omit workspace-only elements
        const baseElements = this.#isStandaloneMode
            ? STANDALONE_COMMON_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker')
            : COMMON_HEADER_ELEMENTS;
        this.headerElements = [
            ...baseElements,
            { type: 'symbolPicker', position: 22, priority: 5 },
            { type: 'symbolFilter', position: 23, priority: 7 },
            { type: 'spacer', key: 'spacer-left', position: 24, alwaysVisible: true },
            { type: 'alertsTabSwitcher', position: 25, priority: 5 },
            { type: 'alertsEnabledToggle', position: 100, priority: 6 },
            { type: 'connectionStatus', position: 995, priority: 995 },
        ];
        // Ideas tab: add button replaces symbol picker; no symbol filter
        this.ideaTabHeaderElements = [
            ...baseElements,
            { type: 'alertsAddIdeaAlert', position: 22, priority: 5 },
            { type: 'spacer', key: 'spacer-left', position: 24, alwaysVisible: true },
            { type: 'alertsTabSwitcher', position: 25, priority: 5 },
            { type: 'alertsEnabledToggle', position: 100, priority: 6 },
            { type: 'connectionStatus', position: 995, priority: 995 },
        ];
        // Always initialize slices so their signals exist (eventBus references them)
        // In standalone mode, this creates signals with null/default values
        this.#channelSlice.init(tabId);
        this.#widgetTabSlice.init(tabId);

        // Always register slices for disposal so their store subscriptions are cleaned up
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        if (this.#isStandaloneMode) {
            (this.#widgetTabSlice.eventBus.getSignal('widget::name') as Signal<string>).value =
                t('widgets::alerts::name');
        }

        if (!this.#isStandaloneMode) {
            // Use effect to react to linkedSymbol changes from store
            // using effect to make the imperative call to #handleLinkedSymbol when linked symbol changes
            let prevLinkedSymbol: string | null = null;
            this.disposableStack.defer(
                effect(() => {
                    const linkedSymbol = this.#widgetState.value?.linkedSymbol ?? null;
                    if (linkedSymbol && linkedSymbol !== prevLinkedSymbol) {
                        prevLinkedSymbol = linkedSymbol;
                        untracked(() => {
                            this.#handleLinkedSymbol(linkedSymbol).catch((err) => {
                                this.logger.error('Failed to handle linked symbol', { linkedSymbol, error: err });
                            });
                        });
                    } else {
                        prevLinkedSymbol = linkedSymbol;
                    }
                }),
            );
        }

        this.logger.info('AlertsWidgetModel initialized', { isStandaloneMode: this.#isStandaloneMode });
    }

    async addAlert(alertData: NewConditionAlertDefinition): Promise<AlertDefinition> {
        const result = await this.alertService.addAlert(alertData);
        if (TRACE) {
            this.logger.trace('Alert added', {
                alertId: result.id,
                instrument: result.type === 'condition' ? instrumentToString(result.instrument) : undefined,
            });
        }
        // Queue focus for the real row that will mount after the AlertService subscription syncs
        this.#pendingFocusAlertIds.add(result.id);
        return result;
    }

    async addAlertForInstrument(instrument: Instrument): Promise<void> {
        const defaultIndicator = this.defaultIndicator.value;
        if (!defaultIndicator) {
            this.logger.warn('No default indicator available to create alert', {
                instrument: instrumentToString(instrument),
            });
            return;
        }

        // Add alert immediately with empty value and disabled (will auto-enable when value is set)
        await this.addAlert({
            type: 'condition',
            instrument,
            condition: {
                type: 'quantitative',
                id: uuid(),
                enabled: true,
                indicator: defaultIndicator,
                min: null,
                max: null,
            },
            enabled: false,
            notifications: [{ type: 'toast' }, { type: 'sound', sound: 'tambourine' }],
        });
    }

    async updateAlert(alertId: string, updates: AlertUpdateData): Promise<AlertDefinition> {
        // Convert UI-level updates to API-level format
        const apiUpdates: Partial<NewConditionAlertDefinition> = {};

        if (updates.condition !== undefined) {
            apiUpdates.condition = updates.condition;
        }
        if (updates.enabled !== undefined) {
            apiUpdates.enabled = updates.enabled;
        }
        if (updates.note !== undefined) {
            apiUpdates.userNote = updates.note ?? '';
        }

        // Handle notification updates - need to get current values and merge
        if (updates.showToast !== undefined || updates.playSound !== undefined || updates.soundType !== undefined) {
            // Find the current alert to get existing notification settings
            const currentViewModel = this.#alertRowViewModels.value.find((vm) => vm.id === alertId);
            if (currentViewModel) {
                const showToast = updates.showToast ?? currentViewModel.showToast.value;
                const playSound = updates.playSound ?? currentViewModel.playSound.value;
                const soundType = updates.soundType ?? currentViewModel.soundType.value;

                const notifications: AlertNotification[] = [];
                if (showToast) {
                    notifications.push({ type: 'toast' });
                }
                if (playSound && soundType) {
                    notifications.push({ type: 'sound', sound: soundType });
                }
                apiUpdates.notifications = notifications;
            }
        }

        const result = await this.alertService.updateAlert(alertId, apiUpdates);
        if (TRACE) {
            this.logger.trace('Alert updated', { alertId });
        }
        return result;
    }

    async removeAlert(alertId: string): Promise<void> {
        await this.alertService.removeAlert(alertId);
        if (TRACE) {
            this.logger.trace('Alert removed', { alertId });
        }
    }

    async setAlertEnabled(alertId: string, enabled: boolean): Promise<void> {
        await this.alertService.updateAlert(alertId, { enabled });
        if (TRACE) {
            this.logger.trace('Alert enabled state changed', { alertId, enabled });
        }
    }

    readonly defaultIndicator = computed(() => {
        const slugMap = this.slugMapService.slugMap.value;
        // Use 'symbol::last' (last traded price) as the default indicator
        const lastPrice = slugMap?.[Slugs.last];
        if (lastPrice && lastPrice.flags.alerting) {
            return { id: lastPrice.id };
        }
        // Fallback to first alerting indicator
        const indicators = this.#alertingIndicators.value;
        return indicators[0] ? { id: indicators[0].id } : null;
    });

    async #handleLinkedSymbol(linkedSymbol: string): Promise<void> {
        // Find an existing alert for this symbol (comparing instrument strings)
        const matchingAlert = this.alertService.conditionAlerts.value.find(
            (a) => instrumentToString(a.instrument) === linkedSymbol,
        );

        if (matchingAlert) {
            // Highlight the matching alert
            this.#highlightedAlertId.value = matchingAlert.id;
            this.logger.info('Highlighting alert for linked symbol', { linkedSymbol, alertId: matchingAlert.id });

            // Clear highlight after a delay
            setTimeout(() => {
                if (this.#highlightedAlertId.value === matchingAlert.id) {
                    this.#highlightedAlertId.value = null;
                }
            }, 3000);
        } else if (this.#autoCreateFromChannel.value) {
            // Auto-create an alert for the symbol if feature flag is enabled
            const instrument: Instrument = { type: 'equity', symbol: linkedSymbol };
            await this.addAlertForInstrument(instrument);
            this.logger.info('Auto-created alert for linked symbol', { linkedSymbol });
        } else {
            this.logger.info('No alert found for linked symbol', { linkedSymbol });
        }
    }

    triggerAlert(alertId: string): void {
        this.alertService.simulateTrigger(alertId);
    }

    get currentSort(): Signal<AlertsSort> {
        return this.#currentSort;
    }

    setSort(sort: AlertsSort): void {
        // Setting #currentSort.value triggers the effect that calls #syncAlertRowViewModels
        this.#currentSort.value = sort;
        if (TRACE) {
            this.logger.trace('setSort: sort changed', { sort });
        }
    }

    moveAlert(fromIndex: number, toIndex: number): void {
        const vms = [...this.#alertRowViewModels.value];
        const lastIndex = vms.length - 1;
        if (fromIndex === toIndex || fromIndex < 0 || fromIndex > lastIndex || toIndex < 0 || toIndex > lastIndex) {
            return;
        }
        const [moved] = vms.splice(fromIndex, 1);
        vms.splice(toIndex, 0, moved);
        this.#currentSort.value = 'manual';
        this.#alertRowViewModels.value = vms;
        if (TRACE) {
            this.logger.trace('moveAlert: moved alert', { fromIndex, toIndex });
        }
    }

    /**
     * Compare two alerts based on the current sort field and direction.
     */
    #compareAlerts(a: ConditionAlertRowViewModelImpl, b: ConditionAlertRowViewModelImpl, sort: AlertsSort): number {
        const [field, direction] = sort.split(' ') as [string, 'asc' | 'desc'];
        const multiplier = direction === 'asc' ? 1 : -1;

        switch (field) {
            case 'createdAt': {
                const aCreatedAt = a.alertModel?.definition.value.createdAt;
                const bCreatedAt = b.alertModel?.definition.value.createdAt;
                if (!aCreatedAt || !bCreatedAt) return 0;
                return aCreatedAt.compareTo(bCreatedAt) * multiplier;
            }
            case 'symbol': {
                const aSymbol = a.instrumentString;
                const bSymbol = b.instrumentString;
                return aSymbol.localeCompare(bSymbol) * multiplier;
            }
            case 'indicator': {
                const aIndicator = this.#getIndicatorName(a);
                const bIndicator = this.#getIndicatorName(b);
                return aIndicator.localeCompare(bIndicator) * multiplier;
            }
            case 'direction': {
                const aDirection = this.#getDirection(a);
                const bDirection = this.#getDirection(b);
                return aDirection.localeCompare(bDirection) * multiplier;
            }
            case 'value': {
                const aValue = this.#getValue(a);
                const bValue = this.#getValue(b);
                // Null values sort to end
                if (aValue === null && bValue === null) return 0;
                if (aValue === null) return 1;
                if (bValue === null) return -1;
                return (aValue - bValue) * multiplier;
            }
            case 'note': {
                const aNote = a.note.value ?? '';
                const bNote = b.note.value ?? '';
                return aNote.localeCompare(bNote) * multiplier;
            }
            case 'enabled': {
                const aEnabled = a.enabled.value ? 1 : 0;
                const bEnabled = b.enabled.value ? 1 : 0;
                return (aEnabled - bEnabled) * multiplier;
            }
            default:
                return 0;
        }
    }

    #getIndicatorName(vm: ConditionAlertRowViewModelImpl): string {
        const condition = vm.condition.value;
        if (condition?.type !== 'quantitative' || !condition.indicator) return '';
        const indicators = this.#alertingIndicators.value;
        const indicator = indicators.find((i) => i.id === condition.indicator?.id);
        return indicator?.name ?? '';
    }

    #getDirection(vm: ConditionAlertRowViewModelImpl): string {
        const condition = vm.condition.value;
        if (condition?.type !== 'quantitative') return '';
        if (condition.max !== null && condition.min === null) return 'below';
        return 'above';
    }

    #getValue(vm: ConditionAlertRowViewModelImpl): number | null {
        const condition = vm.condition.value;
        if (condition?.type !== 'quantitative') return null;
        if (condition.max !== null) return condition.max.value;
        if (condition.min !== null) return condition.min.value;
        return null;
    }

    // =========== Idea Alerts ===========

    get ideaAlerts(): ReadonlySignal<IdeaAlertRowViewModel[]> {
        return this.#ideaAlertRowViewModels;
    }

    get maxIdeas(): ReadonlySignal<number | undefined> {
        return this.#maxIdeas;
    }

    get activeIdeaCount(): ReadonlySignal<number> {
        return this.#activeIdeaCount;
    }

    #syncIdeaAlertRowViewModels(): void {
        const ideaAlertModels = this.alertService.ideaAlerts.value;
        const currentVMs = untracked(() => this.#ideaAlertRowViewModels.value);
        const currentVMMap = new Map<string, IdeaAlertRowViewModelImpl>(currentVMs.map((vm) => [vm.id, vm]));

        const newVMs: IdeaAlertRowViewModelImpl[] = [];
        const seenIds = new Set<string>();

        for (const model of ideaAlertModels) {
            seenIds.add(model.id);
            const existingVm = currentVMMap.get(model.id);
            if (existingVm) {
                newVMs.push(existingVm);
            } else {
                const newVm = this.ideaAlertRowViewModelFactory();
                newVm.initFromIdeaAlertModel(model);
                newVm.setOnStateChange(this.#handleIdeaRowStateChange);
                newVMs.push(newVm);
            }
        }

        for (const vm of currentVMs) {
            if (!seenIds.has(vm.id)) {
                vm[Symbol.dispose]();
            }
        }

        if (this.#ideaManualSort) {
            // Preserve existing order for known VMs; new VMs go at the end
            const currentVmOrder = untracked(() => this.#ideaAlertRowViewModels.value);
            const indexById = new Map<string, number>(currentVmOrder.map((vm, i) => [vm.id, i]));
            newVMs.sort((a, b) => {
                const ai = indexById.get(a.id) ?? -1;
                const bi = indexById.get(b.id) ?? -1;
                if (ai === -1 && bi === -1) return 0;
                if (ai === -1) return 1;
                if (bi === -1) return -1;
                return ai - bi;
            });
        }

        this.#ideaAlertRowViewModels.value = newVMs;
    }

    #handleIdeaRowStateChange = (alertId: string, state: IdeaAlertRowState): void => {
        const maxIdeas = this.#maxIdeas.value;
        let effectiveEnabled = state.enabled;

        if (state.enabled && maxIdeas !== undefined) {
            const activeCount = this.#ideaAlertRowViewModels.value.filter(
                (vm) => vm.id !== alertId && vm.enabled.value,
            ).length;
            if (!canEnableIdeaAlert(activeCount, maxIdeas)) {
                const vm = this.#ideaAlertRowViewModels.value.find((a) => a.id === alertId);
                vm?.dispatch({ type: 'SET_ENABLED', enabled: false });
                effectiveEnabled = false;
            }
        }

        this.alertService
            .updateAlert(alertId, {
                type: 'idea',
                enabled: effectiveEnabled,
                notifications: stateToNotifications(state),
                userNote: state.note ?? '',
                triggerMode: state.triggerMode,
                slowIdeaId: state.slowIdeaId,
                fastIdeaId: state.fastIdeaId,
            } satisfies Partial<NewIdeaAlertDefinition> & { type: 'idea' })
            .catch((err) => {
                this.logger.error('Failed to sync idea alert state change', { alertId, error: err });
            });
    };

    async addIdeaAlert(alertData: NewIdeaAlertDefinition): Promise<AlertDefinition> {
        return await this.alertService.addAlert(alertData);
    }

    moveIdeaAlert(fromIndex: number, toIndex: number): void {
        const vms = [...this.#ideaAlertRowViewModels.value];
        const lastIndex = vms.length - 1;
        if (fromIndex === toIndex || fromIndex < 0 || fromIndex > lastIndex || toIndex < 0 || toIndex > lastIndex) {
            return;
        }
        const [moved] = vms.splice(fromIndex, 1);
        vms.splice(toIndex, 0, moved);
        this.#ideaManualSort = true;
        this.#ideaAlertRowViewModels.value = vms;
        if (TRACE) {
            this.logger.trace('moveIdeaAlert: moved idea alert', { fromIndex, toIndex });
        }
    }

    async removeIdeaAlert(alertId: string): Promise<void> {
        await this.alertService.removeAlert(alertId);
    }
}
